import { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    ArrowRightOutlined,
    CloseOutlined,
    DeleteOutlined,
    ReloadOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { Button, Input, message, Modal, Spin, Switch, Upload } from 'antd';
import classNames from 'classnames';
import {
    ChevronDown,
    ClipboardPaste,
    ExternalLink,
    FileCode2,
    FileSpreadsheet,
    Languages,
    Library,
    Plus,
    UploadCloud,
} from 'lucide-react';
import { baseURL } from '../../../../axios/axios';
import { useDevice } from '../../../../hooks/useDevice';
import { searchReferences, uploadRefPath } from '../../api';
import {
    CUSTOM_SOURCE_TYPES,
    generateReferenceId,
    getSourceConfig,
    MAX_CUSTOM_REFERENCE_COUNT,
    MAX_ENGLISH_SUPPLEMENT,
    MAX_REFERENCE_COUNT,
    MAX_UPLOAD_MB,
    parseReferences,
    refDedupeKey,
    REFERENCE_TYPE,
    stableReferenceId,
} from '../utils';
import styles from './index.module.scss';

// ==================== 常量 ====================

const { TextArea } = Input;

const BATCH_OPTIONS = [5, 10, 20, 'all'];

const PASTE_PLACEHOLDER = `请在此处粘贴文献的查新引文格式（以 [1] [2] 等编号开头，可带「摘要：」）。自定义添加最多 ${MAX_CUSTOM_REFERENCE_COUNT} 条，其中最多选用 ${MAX_REFERENCE_COUNT} 条参与写作。`;

/** 三种「添加我的文献」方式：桌面平铺成三张卡，手机收成一行小按钮（short），各自打开对应的弹窗 */
const ADD_MODES = {
    paste: {
        key: 'paste',
        title: '手动添加',
        short: '手动',
        modalTitle: '手动添加文献',
        desc: '粘贴文献题录，自动识别',
        icon: ClipboardPaste,
    },
    excel: {
        key: 'excel',
        title: '上传Excel文献',
        short: 'Excel',
        modalTitle: '上传 Excel 文献',
        desc: '支持 .xlsx / .xls 文件',
        icon: FileSpreadsheet,
    },
    ris: {
        key: 'ris',
        title: '上传RIS文献',
        short: 'RIS',
        modalTitle: '上传 RIS 文献',
        desc: '知网 / EndNote 等导出的 .ris',
        icon: FileCode2,
    },
};

const UPLOAD_CONFIG = {
    excel: {
        sourceType: REFERENCE_TYPE.EXCEL_UPLOAD,
        exts: ['.xlsx', '.xls'],
        hint: '.xlsx / .xls',
        tip: '每行一条文献题录，可附摘要列；识别后会先列出来，确认后再加入',
    },
    ris: {
        sourceType: REFERENCE_TYPE.RIS_UPLOAD,
        exts: ['.ris'],
        hint: '.ris',
        tip: '可从知网、万方、EndNote、Zotero 等导出 RIS 格式的题录文件',
    },
};

/** 整理检索结果里的来源链接：只保留 http(s)，同 URL / 同名站点去重 */
const normalizeSourceList = (list) => {
    if (!Array.isArray(list)) return [];
    const seenUrls = new Set();
    const seenAnchors = new Set();
    const out = [];
    list.forEach((item) => {
        const url = typeof item?.url === 'string' ? item.url.trim() : '';
        if (!/^https?:\/\//i.test(url) || seenUrls.has(url)) return;
        let anchor = typeof item.anchor === 'string' ? item.anchor.trim() : '';
        if (!anchor) {
            try {
                anchor = new URL(url).hostname.replace(/^www\./, '');
            } catch {
                anchor = '来源';
            }
        }
        if (seenAnchors.has(anchor.toLowerCase())) return;
        seenUrls.add(url);
        seenAnchors.add(anchor.toLowerCase());
        out.push({ anchor, url });
    });
    return out;
};

/** 检索接口返回 → 列表数据（稳定 id：同一题录重新检索后已勾选状态能对上） */
const toAiResults = (list) => {
    const seen = new Map();
    return (Array.isArray(list) ? list : [])
        .filter((ref) => ref?.references)
        .map((ref) => ({
            ...ref,
            id: stableReferenceId(REFERENCE_TYPE.SEARCH_RESULT, ref.references, seen),
            sourceType: REFERENCE_TYPE.SEARCH_RESULT,
            sourceList: normalizeSourceList(ref.sourceList),
        }));
};

const stopPropagation = (e) => e.stopPropagation();

const SourceLinks = memo(function SourceLinks({ sources }) {
    if (!sources?.length) return null;
    return (
        <div className={styles.aiItemSources}>
            <span className={styles.aiSourceLabel}>来源</span>
            {sources.map((s) => (
                <a
                    key={s.url}
                    className={styles.aiSourceLink}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={s.url}
                    onClick={stopPropagation}
                >
                    {s.anchor}
                    <ExternalLink size={10} aria-hidden />
                </a>
            ))}
        </div>
    );
});

const SourceTag = ({ sourceType, className }) => {
    const config = getSourceConfig(sourceType);
    if (!config) return null;
    return (
        <span
            className={classNames(styles.sourceTag, className)}
            style={{
                backgroundColor: config.bgColor,
                color: config.color,
                border: `1px solid ${config.borderColor}`,
            }}
        >
            {config.shortLabel}
        </span>
    );
};

/** 固定底栏挂到 .app-container（主题 CSS 变量的作用域）下，避开外层 transform */
const getPortalTarget = () => document.querySelector('.app-container') || document.body;

/** AI 检索的预计用时（秒），手机上的进度提示按它算进度条 */
const AI_SEARCH_EXPECTED_SECONDS = 30;

/**
 * 手机上的检索进度提示：「正在检索相关文献，大约需要 30 秒」+ 已用秒数 + 进度条。
 * 只在检索进行中挂载，检索结束随 aiLoading 一起卸载。
 */
const SearchProgress = memo(function SearchProgress() {
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
        const startedAt = Date.now();
        const timer = setInterval(() => {
            setElapsed(Math.floor((Date.now() - startedAt) / 1000));
        }, 500);
        return () => clearInterval(timer);
    }, []);

    // 超过预计用时后停在 96%，等接口返回再消失
    const percent = Math.min(96, Math.round((elapsed / AI_SEARCH_EXPECTED_SECONDS) * 100));
    const overtime = elapsed > AI_SEARCH_EXPECTED_SECONDS;

    return (
        <div className={styles.searchProgress} role="status" aria-live="polite">
            <div className={styles.searchProgressHead}>
                <Spin size="small" />
                <span className={styles.searchProgressTitle}>
                    正在检索相关文献，大约需要 {AI_SEARCH_EXPECTED_SECONDS} 秒
                </span>
                <span className={styles.searchProgressTime}>已用 {elapsed} 秒</span>
            </div>
            <div
                className={styles.searchProgressBar}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
            >
                <div className={styles.searchProgressFill} style={{ width: `${percent}%` }} />
            </div>
            <p className={styles.searchProgressHint}>
                {overtime
                    ? '相关文献较多，正在整理，请再稍等一下'
                    : '检索完成后会自动列出可勾选的文献'}
            </p>
        </div>
    );
});

// ==================== 主组件 ====================

/**
 * 第二步：参考资料（选填）。
 * AI 按第一步确认的标题推荐文献；也可手动粘贴 / 上传 Excel / 上传 RIS 添加自己的文献。
 * 最多选用 MAX_REFERENCE_COUNT 条，提交给「确认大纲」生成与下单。
 *
 * @param {string} title 第一步确认的文档标题（AI 推荐按它检索）
 * @param {Array} initialReferences 已选参考资料（退回再进来时保留）
 * @param {Array} initialCustomReferences 自定义添加的候选列表
 * @param {boolean} initialRecommendEnglishLiterature 自动补充外文文献
 * @param {{ title: string, results: Array } | null} aiCache 上次的 AI 推荐结果；标题没变时直接复用
 * @param {(cache) => void} onAiCacheChange
 * @param {({ references, customReferences, recommendEnglishLiterature }) => void} onNext
 * @param {({ references, customReferences, recommendEnglishLiterature }) => void} onPrev 返回上一步（带上当前选择）
 */
export default function ReferenceStep({
    title,
    initialReferences = [],
    initialCustomReferences = [],
    initialRecommendEnglishLiterature = false,
    aiCache,
    onAiCacheChange,
    onNext,
    onPrev,
}) {
    const searchTitle = (title || '').trim();
    // 手机（或窄窗口）：手机上基本只从 AI 推荐里勾选，「添加我的文献」收成一行、已选列表默认收起
    const { isMobile, screenWidth } = useDevice();
    const compact = isMobile || screenWidth <= 768;
    const [selectedOpen, setSelectedOpen] = useState(false);

    // ===== 已选参考资料（最终提交用） =====
    const [references, setReferences] = useState(() =>
        Array.isArray(initialReferences) ? initialReferences : [],
    );

    // ===== 自定义添加候选列表（粘贴 / Excel / RIS 先进这里，最多 200 条；勾选后才进 references） =====
    const [customRefs, setCustomRefs] = useState(() => {
        const pool = Array.isArray(initialCustomReferences) ? [...initialCustomReferences] : [];
        const ids = new Set(pool.map((r) => r.id));
        (initialReferences || []).forEach((ref) => {
            if (CUSTOM_SOURCE_TYPES.has(ref.sourceType) && !ids.has(ref.id)) {
                ids.add(ref.id);
                pool.push(ref);
            }
        });
        return pool.slice(0, MAX_CUSTOM_REFERENCE_COUNT);
    });

    // ===== AI 智能推荐（仅展示，勾选后才加入 references） =====
    const cacheHit = Boolean(aiCache && aiCache.title === searchTitle);
    const [aiResults, setAiResults] = useState(() => (cacheHit ? aiCache.results : []));
    const [aiLoading, setAiLoading] = useState(false);
    const [aiSearched, setAiSearched] = useState(cacheHit);
    const [aiError, setAiError] = useState(false);
    const [aiBatchSelect, setAiBatchSelect] = useState(0);
    const aiSearchReqRef = useRef(0);
    const onAiCacheChangeRef = useRef(onAiCacheChange);
    onAiCacheChangeRef.current = onAiCacheChange;

    const fetchAiSearch = useCallback(async (query) => {
        const q = (query || '').trim();
        if (!q) return;
        const seq = ++aiSearchReqRef.current;
        setAiLoading(true);
        setAiSearched(true);
        setAiError(false);
        setAiBatchSelect(0);
        try {
            const results = toAiResults(await searchReferences(q));
            if (seq !== aiSearchReqRef.current) return;
            setAiResults(results);
            onAiCacheChangeRef.current?.({ title: q, results });
        } catch (error) {
            if (seq !== aiSearchReqRef.current) return;
            setAiResults([]);
            setAiError(true);
            message.error('检索文献失败，请重试');
        } finally {
            if (seq === aiSearchReqRef.current) setAiLoading(false);
        }
    }, []);

    // 进入本步按确认的标题检索一次；标题没变且已有结果时直接复用
    const initialCacheHitRef = useRef(cacheHit);
    useEffect(() => {
        if (!initialCacheHitRef.current) fetchAiSearch(searchTitle);
        return () => {
            // 离开本步后丢弃还没返回的检索
            aiSearchReqRef.current += 1;
        };
    }, []);

    const aiTitleIdPrefix = useId();
    const customTitleIdPrefix = useId();

    const selectedIds = useMemo(() => new Set(references.map((r) => r.id)), [references]);

    const toggleSelectedRef = (item) => {
        if (selectedIds.has(item.id)) {
            setReferences((prev) => prev.filter((r) => r.id !== item.id));
            return;
        }
        if (references.length >= MAX_REFERENCE_COUNT) {
            message.warning(`最多只能选择${MAX_REFERENCE_COUNT}条文献`);
            return;
        }
        setReferences((prev) => (prev.some((r) => r.id === item.id) ? prev : [...prev, item]));
    };

    const toggleAiRef = (item) => {
        setAiBatchSelect(0);
        toggleSelectedRef(item);
    };

    // 快速选择：先移除所有 AI 推荐来源的已选项，再加入前 N 条；再点一次取消
    const handleAiBatchSelect = (opt) => {
        const count = aiBatchSelect === opt ? 0 : opt;
        setAiBatchSelect(count);
        if (aiResults.length === 0) return;
        const nonAiRefs = references.filter((r) => r.sourceType !== REFERENCE_TYPE.SEARCH_RESULT);
        const targetCount = count === 'all' ? aiResults.length : count;
        const combined = [...nonAiRefs, ...aiResults.slice(0, targetCount)];
        if (combined.length > MAX_REFERENCE_COUNT) {
            message.warning(`最多只能选择${MAX_REFERENCE_COUNT}条文献，已自动截取`);
        }
        setReferences(combined.slice(0, MAX_REFERENCE_COUNT));
    };

    const handleRemoveReference = (id) => {
        setAiBatchSelect(0);
        setReferences((prev) => prev.filter((r) => r.id !== id));
    };

    const hasCustomRefs = customRefs.length > 0;

    const handleClearAllReferences = () => {
        Modal.confirm({
            title: '确认清空',
            content: hasCustomRefs
                ? '确定要清空所有已选的参考资料吗？「我的文献」列表会保留，可重新勾选。'
                : '确定要清空所有已选的参考资料吗？',
            okText: '确定',
            cancelText: '取消',
            onOk: () => {
                setReferences([]);
                setAiBatchSelect(0);
                message.success('已清空所有文献');
            },
        });
    };

    const customSelectedCount = customRefs.reduce((n, r) => (selectedIds.has(r.id) ? n + 1 : n), 0);
    const isRefsFull = references.length >= MAX_REFERENCE_COUNT;
    const canSelectAllCustom = customSelectedCount < customRefs.length && !isRefsFull;

    const handleRemoveCustomRef = (id) => {
        setCustomRefs((prev) => prev.filter((r) => r.id !== id));
        setReferences((prev) => prev.filter((r) => r.id !== id));
    };

    const handleCustomSelectAll = () => {
        const unselected = customRefs.filter((r) => !selectedIds.has(r.id));
        if (unselected.length === 0) return;
        const room = Math.max(MAX_REFERENCE_COUNT - references.length, 0);
        if (room === 0) {
            message.warning(`已选文献已达 ${MAX_REFERENCE_COUNT} 条上限`);
            return;
        }
        const toAdd = unselected.slice(0, room);
        setReferences((prev) => [...prev, ...toAdd]);
        if (toAdd.length < unselected.length) {
            message.warning(
                `最多只能选用 ${MAX_REFERENCE_COUNT} 条文献，已选满；还有 ${unselected.length - toAdd.length} 条未勾选`,
            );
        }
    };

    const handleCustomDeselectAll = () => {
        const customIds = new Set(customRefs.map((r) => r.id));
        setReferences((prev) => prev.filter((r) => !customIds.has(r.id)));
    };

    const handleClearCustomRefs = () => {
        Modal.confirm({
            title: '清空我的文献',
            content: `将移除「我的文献」列表里的 ${customRefs.length} 条文献，其中已选用的 ${customSelectedCount} 条也会一并取消。`,
            okText: '清空',
            okButtonProps: { danger: true },
            cancelText: '取消',
            onOk: () => {
                const customIds = new Set(customRefs.map((r) => r.id));
                setCustomRefs([]);
                setReferences((prev) => prev.filter((r) => !customIds.has(r.id)));
                message.success('已清空自己添加的文献');
            },
        });
    };

    // ===== 添加我的文献弹窗：手动添加 / 上传 Excel / 上传 RIS 各开各的 =====
    const [addOpen, setAddOpen] = useState(false);
    const [addMode, setAddMode] = useState('paste');
    const [pasteText, setPasteText] = useState('');
    const [parsedRefs, setParsedRefs] = useState([]);
    const [pasteRecognized, setPasteRecognized] = useState(false);
    const [uploadParsedRefs, setUploadParsedRefs] = useState([]);
    const [uploading, setUploading] = useState(false);
    const customRoom = Math.max(MAX_CUSTOM_REFERENCE_COUNT - customRefs.length, 0);
    const isCustomFull = customRoom === 0;

    const resetAddModal = () => {
        setPasteRecognized(false);
        setParsedRefs([]);
        setPasteText('');
        setUploadParsedRefs([]);
        setUploading(false);
    };

    const openAddModal = (mode) => {
        if (isCustomFull) {
            message.warning(
                `自己添加的文献最多 ${MAX_CUSTOM_REFERENCE_COUNT} 条，已满，请先移除部分再添加`,
            );
            return;
        }
        resetAddModal();
        setAddMode(mode);
        setAddOpen(true);
    };

    const closeAddModal = () => {
        setAddOpen(false);
        resetAddModal();
    };

    /**
     * 把识别出的文献加进候选列表（按题录文本去重、最多 200 条），
     * 并按顺序自动选用，直到已选满 MAX_REFERENCE_COUNT 条。
     */
    const handleAddCustomRefs = (newRefs) => {
        const seen = new Set(customRefs.map(refDedupeKey));
        const fresh = [];
        newRefs.forEach((ref) => {
            const key = refDedupeKey(ref);
            if (!key || seen.has(key)) return;
            seen.add(key);
            fresh.push(ref);
        });
        const duplicateCount = newRefs.length - fresh.length;
        const accepted = fresh.slice(0, customRoom);
        const overflowCount = fresh.length - accepted.length;

        if (accepted.length === 0) {
            if (overflowCount > 0) {
                message.warning(
                    `自己添加的文献最多 ${MAX_CUSTOM_REFERENCE_COUNT} 条，已满，请先移除部分再添加`,
                );
            } else {
                message.info('这些文献已在「我的文献」列表里，未重复添加');
            }
            closeAddModal();
            return;
        }

        const selectRoom = Math.max(MAX_REFERENCE_COUNT - references.length, 0);
        const autoSelected = accepted.slice(0, selectRoom);
        setCustomRefs((prev) => [...prev, ...accepted]);
        if (autoSelected.length > 0) setReferences((prev) => [...prev, ...autoSelected]);

        const notes = [];
        if (duplicateCount > 0) notes.push(`${duplicateCount} 条重复已跳过`);
        if (overflowCount > 0) {
            notes.push(`超出 ${MAX_CUSTOM_REFERENCE_COUNT} 条上限的 ${overflowCount} 条未加入`);
        }
        const suffix = notes.length > 0 ? `（${notes.join('，')}）` : '';
        if (autoSelected.length === accepted.length) {
            message.success(`已添加并选用 ${accepted.length} 条文献${suffix}`);
        } else if (autoSelected.length > 0) {
            message.warning(
                `已添加 ${accepted.length} 条文献，前 ${autoSelected.length} 条已自动选用；最多选用 ${MAX_REFERENCE_COUNT} 条，其余请在「我的文献」列表里手动勾选${suffix}`,
            );
        } else {
            message.warning(
                `已添加 ${accepted.length} 条文献到「我的文献」列表；已选文献已达 ${MAX_REFERENCE_COUNT} 条上限，可在列表里调整勾选${suffix}`,
            );
        }
        closeAddModal();
    };

    const handlePasteRecognize = () => {
        if (!pasteText.trim()) {
            message.error('请输入文献内容');
            return;
        }
        const parsed = parseReferences(pasteText).map((ref, index) => ({
            ...ref,
            id: generateReferenceId(REFERENCE_TYPE.MANUAL_ADD, ref.references, index),
            sourceType: REFERENCE_TYPE.MANUAL_ADD,
        }));
        if (parsed.length === 0) {
            message.error('没有识别到文献，请检查格式（以 [1] [2] 等编号开头）');
            return;
        }
        setParsedRefs(parsed);
        setPasteRecognized(true);
        message.success(`文献识别成功，共 ${parsed.length} 条`);
    };

    const makeUploadProps = (type) => {
        const { sourceType, exts } = UPLOAD_CONFIG[type];
        return {
            name: 'file',
            showUploadList: false,
            accept: exts.join(','),
            multiple: false,
            action: `${baseURL}${uploadRefPath}`,
            beforeUpload: (file) => {
                if (file.size / 1024 / 1024 > MAX_UPLOAD_MB) {
                    message.error(`文件大小不能超过 ${MAX_UPLOAD_MB}MB`);
                    return Upload.LIST_IGNORE;
                }
                const lower = (file.name || '').toLowerCase();
                if (!exts.some((ext) => lower.endsWith(ext))) {
                    message.error(`仅支持 ${exts.join(' / ')} 格式的文件`);
                    return Upload.LIST_IGNORE;
                }
                return true;
            },
            onChange(info) {
                const { status, response, name } = info.file;
                if (status === 'uploading') {
                    setUploading(true);
                    return;
                }
                setUploading(false);
                if (status === 'done') {
                    const refs = response?.code === 0 ? response?.data?.refs : null;
                    if (!Array.isArray(refs) || refs.length === 0) {
                        message.error(response?.msg || `${name} 解析失败：未识别到有效文献`);
                        return;
                    }
                    setUploadParsedRefs(
                        refs
                            .filter((ref) => ref?.references)
                            .map((ref, index) => ({
                                references: ref.references,
                                abstractContent: ref.abstractContent || '',
                                id: generateReferenceId(sourceType, ref.references, index),
                                sourceType,
                            })),
                    );
                    message.success(`已识别 ${refs.length} 条文献`);
                } else if (status === 'error') {
                    message.error(response?.msg || `${name} 上传失败`);
                }
            },
        };
    };

    // ===== 自动补充外文文献（默认关闭）：确认大纲下单时按标题检索英文文献 =====
    const [recommendEnglish, setRecommendEnglish] = useState(
        Boolean(initialRecommendEnglishLiterature),
    );
    const englishSwitchId = useId();
    const englishHintId = useId();
    const englishRoom = Math.min(MAX_ENGLISH_SUPPLEMENT, MAX_REFERENCE_COUNT - references.length);
    const englishHint = !recommendEnglish
        ? `开启后，确认大纲时会按标题检索英文文献，自动补充最多 ${MAX_ENGLISH_SUPPLEMENT} 篇`
        : englishRoom > 0
          ? `确认大纲时按标题检索英文文献，补充最多 ${englishRoom} 篇；检索不到切题的文献时不补充`
          : `已选满 ${MAX_REFERENCE_COUNT} 条文献，不会再补充外文文献`;

    // ===== 固定底栏期间把右下角客服浮钮上移，避免挡住按钮 =====
    useEffect(() => {
        document.body.classList.add('lgfb-writing-footer');
        return () => document.body.classList.remove('lgfb-writing-footer');
    }, []);

    // ===== 提交 / 返回：都带上当前选择，来回切换不丢 =====
    const collect = () => ({
        references,
        customReferences: customRefs,
        recommendEnglishLiterature: recommendEnglish,
    });

    const handleNext = () => onNext?.(collect());
    const handlePrev = () => onPrev?.(collect());

    const aiSelectedCount = references.reduce(
        (n, r) => (r.sourceType === REFERENCE_TYPE.SEARCH_RESULT ? n + 1 : n),
        0,
    );
    const aiListSelectedCount = aiResults.reduce(
        (n, r) => (selectedIds.has(r.id) ? n + 1 : n),
        0,
    );
    // 换了标题重新推荐后，之前按旧标题勾选的文献仍留在已选列表里
    const staleAiCount =
        aiSearched && !aiLoading ? Math.max(aiSelectedCount - aiListSelectedCount, 0) : 0;

    // ===== 弹窗内容 =====
    const renderParsedList = (list, resetLabel, onReset, onConfirm) => (
        <>
            <div className={styles.parsedHeader}>
                <span>
                    已识别 {list.length} 条文献
                    {list.length > customRoom ? (
                        <span className={styles.parsedWarn}>
                            「我的文献」列表还可加入 {customRoom} 条，多出的不会加入
                        </span>
                    ) : null}
                </span>
                <Button size="small" onClick={onReset}>
                    {resetLabel}
                </Button>
            </div>
            <div className={styles.parsedList}>
                {list.map((item, index) => (
                    <div key={item.id} className={styles.parsedItem}>
                        <span className={styles.parsedIndex}>{index + 1}.</span>
                        <span className={styles.parsedText}>{item.references}</span>
                    </div>
                ))}
            </div>
            <div className={styles.modalActions}>
                <Button type="primary" onClick={onConfirm}>
                    确认添加 {list.length} 条文献
                </Button>
            </div>
        </>
    );

    const renderPastePane = () =>
        !pasteRecognized ? (
            <div>
                <TextArea
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    placeholder={PASTE_PLACEHOLDER}
                    className={styles.pasteTextArea}
                    style={{ height: 220, resize: 'none' }}
                    autoFocus
                />
                <div className={styles.modalActions}>
                    <Button
                        type="primary"
                        onClick={handlePasteRecognize}
                        disabled={!pasteText.trim()}
                    >
                        识别文献
                    </Button>
                </div>
            </div>
        ) : (
            renderParsedList(
                parsedRefs,
                '重新粘贴',
                () => {
                    setPasteText('');
                    setParsedRefs([]);
                    setPasteRecognized(false);
                },
                () => handleAddCustomRefs(parsedRefs),
            )
        );

    const renderUploadPane = (type) => {
        const { hint, tip } = UPLOAD_CONFIG[type];
        if (uploadParsedRefs.length > 0) {
            return renderParsedList(
                uploadParsedRefs,
                '重新上传',
                () => setUploadParsedRefs([]),
                () => handleAddCustomRefs(uploadParsedRefs),
            );
        }
        return (
            <>
                <Spin spinning={uploading} tip="正在解析文件...">
                    <Upload.Dragger {...makeUploadProps(type)} className={styles.uploadDragger}>
                        <div className={styles.uploadContent}>
                            <span className={styles.uploadIcon}>
                                <UploadCloud size={36} aria-hidden />
                            </span>
                            <p className={styles.uploadTitle}>点击或拖拽文件到此区域上传</p>
                            <p className={styles.uploadHint}>
                                支持 {hint} 格式，单个文件不超过 {MAX_UPLOAD_MB}MB
                            </p>
                        </div>
                    </Upload.Dragger>
                </Spin>
                <p className={styles.uploadTip}>{tip}</p>
            </>
        );
    };

    const activeMode = ADD_MODES[addMode] || ADD_MODES.paste;
    const ActiveModeIcon = activeMode.icon;

    // ==================== 已选参考资料 + 添加我的文献 ====================
    // 桌面排在 AI 智能推荐上方；手机上挪到 AI 智能推荐下方（手机主要从推荐里勾选）
    const myReferencesCard = (
        <section className={styles.card}>
                <div className={styles.cardHead}>
                    <div className={styles.cardTitleWrap}>
                        <h3 className={styles.cardTitle}>
                            参考资料
                            <span className={styles.optionalTag}>选填</span>
                            <span className={styles.countPill}>
                                已选 <b>{references.length}</b> / {MAX_REFERENCE_COUNT}
                            </span>
                        </h3>
                        <p className={styles.cardHint}>
                            选用的文献会作为大纲与正文的参考依据并列入参考文献；不选也可以直接生成大纲
                        </p>
                    </div>
                    {references.length > 0 && (
                        <button
                            type="button"
                            className={classNames(styles.ghostBtn, styles.danger)}
                            onClick={handleClearAllReferences}
                        >
                            <DeleteOutlined />
                            清空已选
                        </button>
                    )}
                </div>

                {/* 添加我的文献：桌面三种方式平铺成卡；手机收成一行「添加我的文献：手动 · Excel · RIS」 */}
                {compact ? (
                    <div className={styles.addRow}>
                        <span className={styles.addRowLabel}>
                            <Plus size={14} aria-hidden />
                            添加我的文献
                        </span>
                        <div className={styles.addRowBtns}>
                            {Object.values(ADD_MODES).map((mode) => {
                                const Icon = mode.icon;
                                return (
                                    <button
                                        key={mode.key}
                                        type="button"
                                        className={styles.addRowBtn}
                                        data-mode={mode.key}
                                        disabled={isCustomFull}
                                        aria-label={mode.title}
                                        onClick={() => openAddModal(mode.key)}
                                    >
                                        <Icon size={13} aria-hidden />
                                        {mode.short}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <div className={styles.addSection}>
                        <div className={styles.sectionLabel}>
                            <span>添加我的文献</span>
                            <span className={styles.sectionHint}>
                                最多 {MAX_CUSTOM_REFERENCE_COUNT} 条，添加后自动选用
                            </span>
                        </div>
                        <div className={styles.addOptions}>
                            {Object.values(ADD_MODES).map((mode) => {
                                const Icon = mode.icon;
                                return (
                                    <button
                                        key={mode.key}
                                        type="button"
                                        className={styles.addOption}
                                        data-mode={mode.key}
                                        disabled={isCustomFull}
                                        title={
                                            isCustomFull
                                                ? `自己添加的文献最多 ${MAX_CUSTOM_REFERENCE_COUNT} 条，已满`
                                                : undefined
                                        }
                                        onClick={() => openAddModal(mode.key)}
                                    >
                                        <span className={styles.addOptionIcon} aria-hidden>
                                            <Icon size={20} />
                                        </span>
                                        <span className={styles.addOptionText}>
                                            <span className={styles.addOptionTitle}>
                                                {mode.title}
                                            </span>
                                            <span className={styles.addOptionDesc}>{mode.desc}</span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* 自动补充外文文献：一行开关，默认关闭 */}
                <div
                    className={classNames(styles.englishToggle, {
                        [styles.active]: recommendEnglish,
                    })}
                >
                    <span className={styles.englishToggleIcon} aria-hidden>
                        <Languages size={16} />
                    </span>
                    <div className={styles.englishToggleText}>
                        <label htmlFor={englishSwitchId} className={styles.englishToggleTitle}>
                            自动补充外文文献
                        </label>
                        <span id={englishHintId} className={styles.englishToggleHint}>
                            {englishHint}
                        </span>
                    </div>
                    <Switch
                        id={englishSwitchId}
                        checked={recommendEnglish}
                        aria-describedby={englishHintId}
                        onChange={setRecommendEnglish}
                    />
                </div>

                {/* 已选列表：手机上收成一行「已选文献 · 查看 n 条」，点开再看明细 */}
                <div className={classNames(styles.sectionLabel, { [styles.selectedHead]: compact })}>
                    <span>
                        已选文献
                        {compact && references.length > 0 && (
                            <span className={styles.selectedCount}>{references.length}</span>
                        )}
                    </span>
                    {references.length > 0 && !compact && (
                        <span className={styles.sectionHint}>按选用顺序排列，可单独移除</span>
                    )}
                    {references.length > 0 && compact && (
                        <button
                            type="button"
                            className={styles.selectedToggle}
                            aria-expanded={selectedOpen}
                            onClick={() => setSelectedOpen((open) => !open)}
                        >
                            {selectedOpen ? '收起' : '查看明细'}
                            <ChevronDown
                                size={13}
                                aria-hidden
                                className={classNames(styles.selectedChevron, {
                                    [styles.selectedChevronOpen]: selectedOpen,
                                })}
                            />
                        </button>
                    )}
                </div>
                {references.length === 0 ? (
                    <div className={styles.refEmpty}>
                        <Library size={18} aria-hidden />
                        <span>
                            还没有选择参考资料，可以从{compact ? '上方' : '下方'}{' '}
                            <b>AI 智能推荐</b> 里勾选，或通过上方按钮添加自己的文献
                        </span>
                    </div>
                ) : compact && !selectedOpen ? null : (
                    <div className={styles.refList}>
                        {references.map((item, index) => (
                            <div key={item.id} className={styles.refItem}>
                                <span className={styles.refIndex}>{index + 1}</span>
                                <SourceTag sourceType={item.sourceType} />
                                <div className={styles.refText}>{item.references}</div>
                                <button
                                    type="button"
                                    className={styles.removeBtn}
                                    aria-label="移除该文献"
                                    onClick={() => handleRemoveReference(item.id)}
                                >
                                    <CloseOutlined />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* 我的文献（自定义添加候选列表） */}
                {hasCustomRefs && (
                    <div className={styles.customSection}>
                        <div className={styles.listHead}>
                            <div className={styles.listTitleWrap}>
                                <span className={styles.listTitle}>
                                    我的文献
                                    <span className={styles.countPill}>
                                        <b>{customRefs.length}</b> / {MAX_CUSTOM_REFERENCE_COUNT}
                                    </span>
                                </span>
                                <span className={styles.customHint}>
                                    已选用 <b>{customSelectedCount}</b> 条；最多选用{' '}
                                    {MAX_REFERENCE_COUNT} 条，超出的请手动勾选
                                </span>
                            </div>
                            <button
                                type="button"
                                className={classNames(styles.ghostBtn, styles.danger)}
                                onClick={handleClearCustomRefs}
                            >
                                <DeleteOutlined />
                                清空列表
                            </button>
                        </div>

                        <div className={styles.batchRow}>
                            <span className={styles.batchLabel}>快速选择</span>
                            <button
                                type="button"
                                className={styles.batchBtn}
                                disabled={!canSelectAllCustom}
                                onClick={handleCustomSelectAll}
                            >
                                全选
                                {customRefs.length > MAX_REFERENCE_COUNT
                                    ? `（至 ${MAX_REFERENCE_COUNT} 条）`
                                    : ''}
                            </button>
                            <button
                                type="button"
                                className={styles.batchBtn}
                                disabled={customSelectedCount === 0}
                                onClick={handleCustomDeselectAll}
                            >
                                取消全选
                            </button>
                        </div>

                        <div className={styles.customList}>
                            {customRefs.map((item, index) => {
                                const checked = selectedIds.has(item.id);
                                const titleId = `${customTitleIdPrefix}-${index}`;
                                return (
                                    <div
                                        key={item.id}
                                        className={classNames(styles.aiItem, styles.customItem, {
                                            [styles.selected]: checked,
                                        })}
                                        onClick={() => toggleSelectedRef(item)}
                                    >
                                        <button
                                            type="button"
                                            role="checkbox"
                                            aria-checked={checked}
                                            aria-labelledby={titleId}
                                            className={classNames(styles.checkbox, {
                                                [styles.checked]: checked,
                                            })}
                                        />
                                        <div className={styles.aiItemBody}>
                                            <div id={titleId} className={styles.aiItemTitle}>
                                                <SourceTag
                                                    sourceType={item.sourceType}
                                                    className={styles.customItemTag}
                                                />
                                                {item.references}
                                            </div>
                                            {item.abstractContent && (
                                                <div className={styles.aiItemAbstract}>
                                                    {item.abstractContent}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.removeBtn}
                                            aria-label="从我的文献列表移除该文献"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveCustomRef(item.id);
                                            }}
                                        >
                                            <CloseOutlined />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </section>
    );

    // ==================== AI 智能推荐 ====================
    const aiCard = (
        <section className={styles.card}>
                <div className={styles.cardHead}>
                    <div className={styles.cardTitleWrap}>
                        <h3 className={styles.cardTitle}>
                            <SearchOutlined className={styles.cardTitleIcon} />
                            AI 智能推荐
                            {aiResults.length > 0 && (
                                <span className={styles.countPill}>
                                    已选 <b>{aiListSelectedCount}</b> / {aiResults.length}
                                </span>
                            )}
                        </h3>
                        <p className={styles.cardHint}>
                            按标题「<span className={styles.searchTitle}>{searchTitle}</span>
                            」检索的相关文献，勾选即可选用
                        </p>
                        {staleAiCount > 0 && (
                            <p className={styles.staleHint}>
                                已选文献里还有 {staleAiCount} 条是按之前的标题推荐的，如不需要可在上方移除
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        className={styles.ghostBtn}
                        disabled={aiLoading || !searchTitle}
                        onClick={() => fetchAiSearch(searchTitle)}
                    >
                        {aiLoading ? <Spin size="small" /> : <ReloadOutlined />}
                        重新检索
                    </button>
                </div>

                {!aiLoading && aiResults.length > 0 && (
                    <div className={styles.batchRow}>
                        <span className={styles.batchLabel}>快速选择</span>
                        {BATCH_OPTIONS.map((opt) => (
                            <button
                                key={opt}
                                type="button"
                                className={classNames(styles.batchBtn, {
                                    [styles.active]: aiBatchSelect === opt,
                                })}
                                disabled={opt !== 'all' && aiResults.length < opt}
                                aria-pressed={aiBatchSelect === opt}
                                onClick={() => handleAiBatchSelect(opt)}
                            >
                                {opt === 'all' ? '全选' : `前 ${opt} 篇`}
                            </button>
                        ))}
                    </div>
                )}

                {/* 检索中：手机上给带秒数 / 进度条的明确提示，桌面保持原来的一行提示 */}
                {aiLoading &&
                    (compact ? (
                        <SearchProgress />
                    ) : (
                        <div className={styles.aiLoading}>
                            <Spin size="small" />
                            <span>正在根据标题检索相关文献...</span>
                        </div>
                    ))}

                {!aiLoading && aiResults.length > 0 && (
                    <div className={styles.aiList}>
                        {aiResults.map((item, index) => {
                            const checked = selectedIds.has(item.id);
                            const titleId = `${aiTitleIdPrefix}-${index}`;
                            return (
                                <div
                                    key={item.id}
                                    className={classNames(styles.aiItem, {
                                        [styles.selected]: checked,
                                    })}
                                    onClick={() => toggleAiRef(item)}
                                >
                                    <button
                                        type="button"
                                        role="checkbox"
                                        aria-checked={checked}
                                        aria-labelledby={titleId}
                                        className={classNames(styles.checkbox, {
                                            [styles.checked]: checked,
                                        })}
                                    />
                                    <div className={styles.aiItemBody}>
                                        <div id={titleId} className={styles.aiItemTitle}>
                                            {item.references}
                                        </div>
                                        {item.abstractContent && (
                                            <div className={styles.aiItemAbstract}>
                                                {item.abstractContent}
                                            </div>
                                        )}
                                        <SourceLinks sources={item.sourceList} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {!aiLoading && aiSearched && aiResults.length === 0 && (
                    <div className={styles.emptyHint}>
                        {aiError ? (
                            <>
                                <span>文献检索失败，可稍后重试，或先添加自己的文献</span>
                                <button
                                    type="button"
                                    className={styles.ghostBtn}
                                    onClick={() => fetchAiSearch(searchTitle)}
                                >
                                    <ReloadOutlined />
                                    重试
                                </button>
                            </>
                        ) : (
                            <span>未找到相关文献，可返回上一步调整标题，或添加自己的文献</span>
                        )}
                    </div>
                )}
            </section>
    );

    return (
        <div className={styles.page}>
            {compact ? (
                <>
                    {aiCard}
                    {myReferencesCard}
                </>
            ) : (
                <>
                    {myReferencesCard}
                    {aiCard}
                </>
            )}

            {/* ==================== 固定底部操作栏 ==================== */}
            {createPortal(
                <div className={styles.footerBar}>
                    <button type="button" className={styles.pill} onClick={handlePrev}>
                        上一步
                    </button>
                    <div className={styles.footerSummary}>
                        <span>
                            {references.length > 0 ? (
                                <>
                                    已选 <b>{references.length}</b> 份参考资料
                                </>
                            ) : (
                                '未选参考资料'
                            )}
                        </span>
                        {references.length > 0 && customSelectedCount > 0 ? (
                            <>
                                <span className={styles.summaryDot}>·</span>
                                <span>
                                    AI 推荐 {aiSelectedCount} · 我的文献 {customSelectedCount}
                                </span>
                            </>
                        ) : null}
                        {recommendEnglish ? (
                            <>
                                <span className={styles.summaryDot}>·</span>
                                <span>补充外文文献</span>
                            </>
                        ) : null}
                    </div>
                    <button type="button" className={styles.ctaBtn} data-track="references_next" onClick={handleNext}>
                        <span>下一步：生成大纲</span>
                        <ArrowRightOutlined className={styles.ctaArrow} />
                    </button>
                </div>,
                getPortalTarget(),
            )}

            {/* ==================== 添加我的文献弹窗 ==================== */}
            <Modal
                title={
                    <span className={styles.modalTitle}>
                        <ActiveModeIcon size={16} aria-hidden />
                        {activeMode.modalTitle}
                    </span>
                }
                width={720}
                open={addOpen}
                onCancel={closeAddModal}
                footer={null}
                destroyOnHidden
            >
                {addMode === 'paste' && renderPastePane()}
                {addMode === 'excel' && renderUploadPane('excel')}
                {addMode === 'ris' && renderUploadPane('ris')}
            </Modal>
        </div>
    );
}
